/**
 * Centralized price sync for the theme blocks PDP.
 * Liquid handles the initial render — this module only runs on user-driven
 * state changes (variant, quantity, subscription).
 *
 * Targets:
 *   - [data-elixir-price-block]  → main price display block
 *   - [data-elixir-atc-price]    → add-to-cart button text span
 */
(function () {
  "use strict";

  var rafPending = false;
  var pendingVariant = null;

  // ── DOM target lookup ───────────────────────────────────────────────────────

  /** Nearest product section / scope for the main PDP form (avoids the wrong product on complex layouts). */
  function getProductRoot() {
    if (typeof Elixir_GetProductForm === "function") {
      var f = Elixir_GetProductForm();
      if (f) {
        var sec = f.closest(".shop-product-section, section.shopify-section");
        if (sec) return sec;
        return f;
      }
    }
    return null;
  }

  /**
   * 1) Prefer [data-elixir-price-block] within the form's product area, then document.
   * 2) Fallback: any .shop-product-price-container / ancestor of .shop-product-price-block
   *    (legacy or custom layouts without the data attribute, or when the first global match is wrong).
   */
  function getPriceBlock() {
    var root = getProductRoot();
    var el = null;
    if (root) {
      el = root.querySelector("[data-elixir-price-block]");
    }
    if (!el) {
      el = document.querySelector("[data-elixir-price-block]");
    }
    if (el) return el;

    function fallbackFromBase(base) {
      if (!base) return null;
      var sp = base.querySelector(".shop-product-price-block");
      if (!sp) return null;
      return sp.closest(".shop-product-price-container") || sp.parentElement;
    }

    return fallbackFromBase(root) || fallbackFromBase(document) || (function () {
      var any = document.querySelector(".shop-product-price-block");
      if (!any) return null;
      return any.closest(".shop-product-price-container") || any.parentElement;
    })();
  }

  function getAtcTextEl() {
    return document.querySelector("[data-elixir-atc-price]");
  }

  function getAtcBaseLabel(btn) {
    return (
      (btn && (btn.dataset.baseLabel || btn.dataset.persistentText || btn.dataset.originalText)) ||
      (window.variantStrings && window.variantStrings.addToCart) ||
      "Add to cart"
    );
  }

  function getAtcSoldOutLabel(btn) {
    return (btn && btn.dataset.soldOutLabel) || (window.variantStrings && window.variantStrings.soldOut) || "Sold out";
  }

  // ── Form / state helpers ────────────────────────────────────────────────────

  function getVariants() {
    // Prefer a cached reference on the price block container (set by Liquid embed)
    var block = getPriceBlock();
    if (block && block.__elixirVariants) return block.__elixirVariants;
    // Fallback: resolve from the window cache populated by individual blocks
    return window.__elixirProductVariants || [];
  }

  function getVariantFromForm() {
    var id = typeof Elixir_GetProductFormVariantId === "function" ? Elixir_GetProductFormVariantId() : null;
    var variants = getVariants();
    if (!id || !variants.length) return variants[0] || null;
    return (
      variants.find(function (v) {
        return String(v.id) === String(id);
      }) ||
      variants[0] ||
      null
    );
  }

  function getQuantity() {
    var form = typeof Elixir_GetProductForm === "function" ? Elixir_GetProductForm() : null;
    if (!form) return 1;
    var input = form.querySelector('input[name="quantity"]');
    if (!input && form.getAttribute('id')) {
      input = document.querySelector('input[name="quantity"][form="' + form.getAttribute('id') + '"]');
    }
    var qty = input ? parseInt(input.value, 10) : 1;
    return isNaN(qty) || qty < 1 ? 1 : qty;
  }

  // ── Price block animation ────────────────────────────────────────────────────

  function animatePriceBlock(priceBlock) {
    priceBlock.classList.remove("elxr-price-updating");
    void priceBlock.offsetWidth; // force reflow to restart animation
    priceBlock.classList.add("elxr-price-updating");
  }

  // ── Price block DOM update ──────────────────────────────────────────────────

  function updatePriceBlock(priceBlock, result) {
    var priceEl = priceBlock.querySelector(".shop-product-price-block");
    var compareEl = priceBlock.querySelector(".shop-compare-price");
    var saveBadge = priceBlock.querySelector(".shop-save-price");
    var saveTextEl = priceBlock.querySelector("[data-save-text]");

    var priceTotal = result.price;
    var compareTotal = result.compareAtPrice;
    var isOnSale = compareTotal > 0 && compareTotal > priceTotal;

    if (priceEl) {
      priceEl.textContent = window.Elixir_FormatMoney(priceTotal);
      priceEl.classList.toggle("on-sale", isOnSale);
    }

    if (compareEl) {
      if (isOnSale) {
        compareEl.textContent = window.Elixir_FormatMoney(compareTotal);
        compareEl.style.display = "";
      } else {
        compareEl.style.display = "none";
      }
    }

    if (saveBadge) {
      if (isOnSale) {
        saveBadge.style.removeProperty("display");
        if (saveTextEl) {
          var fmt = priceBlock.dataset.saveFormat || "SAVE {savings_percent}";
          var pct = Math.round(((compareTotal - priceTotal) / compareTotal) * 100) + "%";
          var amt = window.Elixir_FormatMoney(compareTotal - priceTotal);
          saveTextEl.textContent = fmt.replace("{savings_percent}", pct).replace("{savings_amount}", amt);
        }
      } else {
        saveBadge.style.setProperty("display", "none", "important");
      }
    }
  }

  // ── ATC button text update ──────────────────────────────────────────────────

  function updateAtcButton(atcTextEl, result, variant) {
    var btn = atcTextEl.closest(".shop-add-to-cart-button");
    var persistentText = getAtcBaseLabel(btn);
    var priceStyle = (btn && btn.dataset.priceStyle) || "current";
    var showComparePrice = !btn || btn.dataset.showComparePrice !== "false";

    if (variant && variant.available === false) {
      if (btn) {
        btn.classList.add("sold-out");
        btn.setAttribute("disabled", "disabled");
        btn.setAttribute("aria-disabled", "true");
      }
      atcTextEl.textContent = getAtcSoldOutLabel(btn);
      return;
    }

    if (btn) {
      btn.classList.remove("sold-out");
      if (!btn.classList.contains("loading")) {
        btn.removeAttribute("disabled");
        btn.removeAttribute("aria-disabled");
      }
    }

    var priceStr = window.Elixir_FormatMoney(result.price);
    var comparePriceStr =
      showComparePrice && result.compareAtPrice > result.price && result.compareAtPrice > 0
        ? window.Elixir_FormatMoney(result.compareAtPrice)
        : null;
    var html = persistentText;

    if (priceStyle === "pill") {
      html += ' <span class="atc-price-pill"><span class="current-price">' + priceStr + "</span>";
      if (comparePriceStr) {
        html += ' <span class="compare-price">' + comparePriceStr + "</span>";
      }
      html += "</span>";
    } else {
      html += ' <span class="current-price">\u2022 ' + priceStr + "</span>";
      if (comparePriceStr) {
        html += ' <span class="compare-price">(' + comparePriceStr + ")</span>";
      }
    }
    atcTextEl.innerHTML = html;
  }

  // ── Core update ────────────────────────────────────────────────────────────

  function doUpdate(variant) {
    if (typeof window.Elixir_GetDisplayPrice !== "function" || typeof window.Elixir_FormatMoney !== "function") {
      if (!window.__elixirPricingReady) {
        document.addEventListener(
          "elixir-pricing-ready",
          function once() {
            document.removeEventListener("elixir-pricing-ready", once);
            doUpdate(variant);
          },
          { once: true },
        );
      } else {
        // Flag set but globals not on window yet (same turn as first script) — one deferred try, no infinite loop
        setTimeout(function () {
          if (typeof window.Elixir_GetDisplayPrice === "function" && typeof window.Elixir_FormatMoney === "function") {
            doUpdate(variant);
          }
        }, 0);
      }
      return;
    }

    var v = variant || getVariantFromForm();
    if (!v) return;

    var priceBlock = getPriceBlock();
    var atcTextEl = getAtcTextEl();

    // Nothing to update if neither target is in the DOM
    if (!priceBlock && !atcTextEl) return;

    // ── Price-override channel ────────────────────────────────────────────────
    // When an active override exists (e.g. from Quantity Break's custom pricing),
    // render those final totals directly instead of recomputing from variant price.
    // The override already has subscription discounts applied, so we only need the
    // variant for availability/sold-out state checks.
    var override = window.Elixir_PriceOverride ? window.Elixir_PriceOverride.get() : null;
    if (override) {
      var overrideResult = { price: override.price, compareAtPrice: override.compareAtPrice };

      if (priceBlock) {
        var priceElOvr = priceBlock.querySelector(".shop-product-price-block");
        var compareElOvr = priceBlock.querySelector(".shop-compare-price");
        var oldPriceOvr = priceElOvr ? priceElOvr.textContent : "";
        var oldCompareOvr = compareElOvr ? compareElOvr.textContent : "";

        updatePriceBlock(priceBlock, overrideResult);

        var newPriceOvr = priceElOvr ? priceElOvr.textContent : "";
        var newCompareOvr = compareElOvr ? compareElOvr.textContent : "";

        if (oldPriceOvr !== newPriceOvr || oldCompareOvr !== newCompareOvr) {
          animatePriceBlock(priceBlock);
        }
      }

      if (atcTextEl) {
        updateAtcButton(atcTextEl, overrideResult, v);
      }
      return;
    }

    // ── Standard variant-based price calculation ──────────────────────────────
    var qty = getQuantity();
    var compareAt = v.compare_at_price != null ? v.compare_at_price : v.compareAtPrice != null ? v.compareAtPrice : 0;

    var multiplyByQuantity = priceBlock ? priceBlock.dataset.multiplyByQuantity !== "false" : true;
    var applySubscriptionDiscount = priceBlock ? priceBlock.dataset.applySubscriptionDiscount !== "false" : true;

    var result = window.Elixir_GetDisplayPrice({
      price: v.price,
      quantity: qty,
      multiplyByQuantity: multiplyByQuantity,
      compareAtPrice: compareAt,
      applySubscriptionDiscount: applySubscriptionDiscount,
      variantId: v.id,
    });

    if (priceBlock) {
      var priceEl = priceBlock.querySelector(".shop-product-price-block");
      var compareEl = priceBlock.querySelector(".shop-compare-price");
      var oldPrice = priceEl ? priceEl.textContent : "";
      var oldCompare = compareEl ? compareEl.textContent : "";

      updatePriceBlock(priceBlock, result);

      var newPrice = priceEl ? priceEl.textContent : "";
      var newCompare = compareEl ? compareEl.textContent : "";

      if (oldPrice !== newPrice || oldCompare !== newCompare) {
        animatePriceBlock(priceBlock);
      }
    }
    if (atcTextEl) {
      var atcBtn = atcTextEl.closest(".shop-add-to-cart-button");
      var atcMultiply = !atcBtn || atcBtn.dataset.multiplyByQuantity !== "false";
      var atcSubscription = !atcBtn || atcBtn.dataset.applySubscriptionDiscount !== "false";

      if (atcMultiply !== multiplyByQuantity || atcSubscription !== applySubscriptionDiscount) {
        var atcResult = window.Elixir_GetDisplayPrice({
          price: v.price,
          quantity: qty,
          multiplyByQuantity: atcMultiply,
          compareAtPrice: compareAt,
          applySubscriptionDiscount: atcSubscription,
          variantId: v.id,
        });
        updateAtcButton(atcTextEl, atcResult, v);
      } else {
        updateAtcButton(atcTextEl, result, v);
      }
    }
  }

  // ── rAF-batched schedule ────────────────────────────────────────────────────

  function scheduleUpdate(variant) {
    if (variant) pendingVariant = variant;
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () {
      rafPending = false;
      var v = pendingVariant;
      pendingVariant = null;
      doUpdate(v);
    });
  }

  // ── Event listeners ─────────────────────────────────────────────────────────

  document.addEventListener("simple-variant-picker:variantChange", function (e) {
    var v = e.detail && e.detail.variant;
    if (v && typeof Elixir_SetProductFormVariantId === "function") {
      Elixir_SetProductFormVariantId(v.id);
    }
    pendingVariant = null;
    doUpdate(v || null);
  });

  document.addEventListener("quantity:changed", function () {
    scheduleUpdate(null);
  });

  document.addEventListener("quantity_break:changed", function () {
    scheduleUpdate(null);
  });

  document.addEventListener("subscriptionContext:updated", function () {
    scheduleUpdate(null);
  });

  // Re-render whenever the QB price-override channel is updated or cleared.
  document.addEventListener("elixir:price-override-changed", function () {
    scheduleUpdate(null);
  });

  // Deferred scripts execute in document order. The QB block's script tag is
  // rendered inside {% content_for 'blocks' %} which precedes this script tag
  // in the HTML, so the QB block initialises — and commits its preselected
  // quantity to the form — before our listeners are registered. Any events it
  // fires during init are therefore missed. Do one initial pass here to pick up
  // whatever form state was already committed by earlier deferred scripts.
  scheduleUpdate(null);
})();
